/**
 * EditableMultiselectCell - A self-contained inline-editable multiselect cell for tables
 *
 * Mirrors EditableDropdownCell pattern:
 * - Click-to-edit: container onClick calls onCellEdit(context) so table state is set on first click
 * - Read-only: shows as many tags as fit in the column width, then "+N"
 * - When editing: single TableAutocomplete for both API-backed and static options
 * - Saves on Enter key press or click-away
 * - Shows selected values as deletable Chip tags in edit mode
 * - Sorts options with selected first, then alphabetically
 *
 * Used for Categories, custom field multiselects, and other multiselect columns.
 */

import { useState, useCallback, useRef, useEffect, useMemo, memo, type CSSProperties } from 'react';
import type { CellContext } from '@tanstack/react-table';
import { ClickAwayListener } from '@mui/base';
import { Tooltip } from 'components';
import { CaretDown } from '@phosphor-icons/react';
import { t } from '@lingui/macro';
import { toast } from 'react-toastify';
import type { ComboBoxApi, ComboBoxOption } from 'components/inputs/types';
import { useEscapeToClose, useEnterToClose } from '../hooks';
import { useEditableContextOptional } from './OptimizedTableProvider';
import { clearEditOnSuccess } from '../utils/inlineEditUtils';
import { CellContainer, MultiselectEditorContainer } from '../styles';
import { TagsWithOverflow } from './TagsWithOverflow';
import {
  MULTISELECT_INPUT_SX,
  MULTISELECT_PAPER_PROPS,
  MULTISELECT_POPPER_PROPS,
  MULTISELECT_CELL_BASE,
  MULTISELECT_CELL_HOVER,
  MULTISELECT_CELL_EDITING,
  MULTISELECT_CELL_ERROR,
  MULTISELECT_CELL_DISABLED,
  MULTISELECT_PLACEHOLDER_STYLE,
} from './multiselectInlineEditStyles';
import { renderMultiselectTags } from './multiselectInlineEditUtils';
import { TableAutocomplete } from './TableAutocomplete';

export type EditableMultiselectCellProps<T extends { id: number | string }> = {
  context: CellContext<T, unknown>;
  /** Currently selected values for display and edit */
  value: ComboBoxOption[];
  /** Callback when selection changes - receives row and partial update with new value array */
  onUpdate: (row: T, update: Partial<T>) => void;
  /** Tooltip text (e.g. full list of labels) */
  tooltipTitle?: string;
  /** Optional when used inside OptimizedTableProvider (read from context) */
  editableRowIndex?: number | null;
  editableColumnId?: string | null;
  onCellEdit?: (context: CellContext<T, unknown>) => void;
  onClickAway?: () => void;
  placeholder?: string;
  /** API config for options (API-backed path); when set, TableAutocomplete fetches options from API */
  api?: ComboBoxApi | null;
  /** Static options (used when api is not set); TableAutocomplete uses these directly */
  options?: ComboBoxOption[];
  /** Column id used for the update payload key and Autocomplete id */
  columnId: string;
  /** Whether this field is required (hides Clear option in dropdown) */
  required?: boolean;
  /** Column label for error toasts (e.g. when clearing required field) */
  columnLabel?: string;
  /** Whether to show error styling on the cell */
  error?: boolean;
  /** Whether the cell is disabled (non-editable, muted styling) */
  disabled?: boolean;
};

const EditableMultiselectCellInner = <T extends { id: number | string }>({
  context,
  value,
  onUpdate,
  tooltipTitle,
  editableRowIndex: editableRowIndexProp,
  editableColumnId: editableColumnIdProp,
  onCellEdit: onCellEditProp,
  onClickAway: onClickAwayProp,
  placeholder = '',
  api,
  options = [],
  columnId,
  required,
  columnLabel,
  error: errorProp,
  disabled: disabledProp,
}: EditableMultiselectCellProps<T>) => {
  const editableCtx = useEditableContextOptional();
  const editableState = editableCtx?.editableState ?? null;
  const setEditableState = editableCtx?.setEditableState ?? ((): void => undefined);

  const editableRowIndex = editableRowIndexProp ?? editableState?.rowIndex ?? null;
  const editableColumnId = editableColumnIdProp ?? editableState?.columnId ?? null;
  const isEditing =
    context.row.index === editableRowIndex && context.column.id === editableColumnId;

  const onCellEdit =
    onCellEditProp ??
    (() => {
      setEditableState({ rowIndex: context.row.index, columnId: context.column.id });
    });
  const onClickAway =
    onClickAwayProp ??
    (() => {
      setEditableState({ rowIndex: null, columnId: null });
    });

  const [isHovered, setIsHovered] = useState(false);

  // Track local selection state for API mode (to support save on click-away)
  const [localValue, setLocalValue] = useState<ComboBoxOption[]>(value);
  const isEditingRef = useRef(false);

  // Sync local value when prop changes (but not during editing)
  useEffect(() => {
    if (!isEditingRef.current) {
      setLocalValue(value);
    }
  }, [value]);

  // Reset editing ref when editing state changes
  useEffect(() => {
    if (isEditing) {
      isEditingRef.current = true;
      setLocalValue(value);
    } else {
      isEditingRef.current = false;
    }
  }, [isEditing, value]);

  // Check if selection has changed from original value
  const hasSelectionChanged = useCallback(() => {
    const currentIds = new Set(value.map(v => String(v.id)));
    const localIds = new Set(localValue.map(v => String(v.id)));
    return currentIds.size !== localIds.size || [...localIds].some(id => !currentIds.has(id));
  }, [localValue, value]);

  // Handle save - called on Enter key or click-away (both API and static paths)
  const handleSave = useCallback(() => {
    if (hasSelectionChanged()) {
      const updateValue = localValue.map(v => ({
        id: v.id,
        name: v.label ?? '',
      }));
      const result = onUpdate(context.row.original, { [columnId]: updateValue } as Partial<T>);
      clearEditOnSuccess(result ?? undefined, () =>
        setEditableState({ rowIndex: null, columnId: null })
      );
    }
    isEditingRef.current = false;
    onClickAway();
  }, [
    hasSelectionChanged,
    localValue,
    onUpdate,
    context.row.original,
    columnId,
    onClickAway,
    setEditableState,
  ]);

  const handleClick = useCallback(() => {
    if (!isEditing && !disabledProp && context.row.original.id !== -1) {
      onCellEdit(context);
    }
  }, [isEditing, disabledProp, context, onCellEdit]);

  const handleClickAway = useCallback(() => {
    handleSave();
  }, [handleSave]);

  const handleEscape = useCallback(() => {
    // Reset to original value on escape
    setLocalValue(value);
    isEditingRef.current = false;
    onClickAway();
  }, [value, onClickAway]);

  useEscapeToClose(isEditing, handleEscape);

  // Use capture-phase Enter key handler (both API and static paths)
  useEnterToClose(isEditing, handleSave);

  const colWidth = context.column.getSize();
  // Memoize read-only content to prevent unnecessary re-renders
  const readOnlyContent = useMemo(
    () => (
      <CellContainer>
        {value.length === 0 ? (
          <span style={MULTISELECT_PLACEHOLDER_STYLE}>_</span>
        ) : (
          <TagsWithOverflow values={value} columnWidth={colWidth} />
        )}
      </CellContainer>
    ),
    [value, colWidth]
  );

  // Memoize autocomplete onChange handler (handles "Clear" option - only clears selection; exit is on click-away or Enter)
  const handleAutocompleteChange = useCallback(
    (_: unknown, newValue: ComboBoxOption[] | null) => {
      const hasClear = newValue?.some(v => String(v?.id) === '__clear__');
      if (hasClear) {
        if (required) {
          toast.error(t`${columnLabel || 'This field'} is required and cannot be cleared`);
          return;
        }
        setLocalValue([]);
        return;
      }
      setLocalValue(newValue ?? []);
    },
    [required, columnLabel]
  );

  // Memoize autocomplete onClose handler
  const handleAutocompleteClose = useCallback(
    (_event: React.SyntheticEvent, reason: string) => {
      // Only close on escape - click-away is handled by ClickAwayListener
      if (reason === 'escape') {
        handleEscape();
      }
    },
    [handleEscape]
  );

  const editorContent = (
    <MultiselectEditorContainer>
      <TableAutocomplete
        id={`${columnId}-inline`}
        placeholder={placeholder}
        api={api ?? undefined}
        options={options}
        variant="standard"
        labelSeparate
        multiple
        forcePopupIcon
        popupIcon={<CaretDown size={20} />}
        value={localValue}
        sortSelectedFirst
        selectedValues={localValue}
        renderTags={renderMultiselectTags}
        open={isEditing}
        onClose={handleAutocompleteClose}
        onChange={handleAutocompleteChange}
        disableCloseOnSelect
        disableClearable
        clearOption={{ id: '__clear__', label: t`Clear` }}
        showClearOption={localValue.length > 0 && !required}
        isOptionEqualToValue={(option, val) => {
          if (!option || !val) return false;
          if (option.id === '__clear__' || val.id === '__clear__') return false;
          return String(option.id) === String(val.id);
        }}
        getOptionLabel={option => {
          if (typeof option === 'string') return option;
          return option?.label ?? '';
        }}
        sx={MULTISELECT_INPUT_SX}
        componentsProps={{
          ...MULTISELECT_PAPER_PROPS,
          ...MULTISELECT_POPPER_PROPS,
        }}
      />
    </MultiselectEditorContainer>
  );

  const content = isEditing ? (
    <ClickAwayListener onClickAway={handleClickAway}>{editorContent}</ClickAwayListener>
  ) : (
    readOnlyContent
  );

  const showHover = isHovered && !isEditing && !disabledProp;

  const cellStateStyle: CSSProperties = {
    ...MULTISELECT_CELL_BASE,
    ...(showHover && !errorProp ? MULTISELECT_CELL_HOVER : {}),
    ...(showHover && errorProp
      ? {
          backgroundColor: MULTISELECT_CELL_HOVER.backgroundColor,
          cursor: MULTISELECT_CELL_HOVER.cursor,
        }
      : {}),
    ...(disabledProp ? MULTISELECT_CELL_DISABLED : {}),
    ...(isEditing ? MULTISELECT_CELL_EDITING : {}),
    ...(errorProp ? MULTISELECT_CELL_ERROR : {}),
  };

  return (
    <Tooltip title={isEditing ? '' : tooltipTitle ?? ''}>
      <CellContainer
        onClick={handleClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={cellStateStyle}
      >
        {content}
      </CellContainer>
    </Tooltip>
  );
};

// Wrap with memo for performance optimization
export const EditableMultiselectCell = memo(EditableMultiselectCellInner) as <
  T extends { id: number | string },
>(
  props: EditableMultiselectCellProps<T>
) => JSX.Element;
